import React, { useMemo, useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, Form, Row, Col, Input, Select, message } from 'antd';
import { getCustomerList, getProjectList } from '../../../../services/project';
import { ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getWorkOrderTemplates } from '../../../../services/workorders';

const { Option } = Select;

const TemplateListPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [category, setCategory] = useState<'customer'|'internal'>('customer');
  const [insTplOptions, setInsTplOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [insTplLoading, setInsTplLoading] = useState(false);
  const [opTypeMap, setOpTypeMap] = useState<Record<string, string>>({});
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [projectOptions, setProjectOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [customerLabelById, setCustomerLabelById] = useState<Record<number, string>>({});
  const [projectLabelById, setProjectLabelById] = useState<Record<number, string>>({});
  const opTypeOptions = useMemo(() => {
    try {
      return Object.entries(opTypeMap).map(([code, name]) => ({
        value: code,
        label: `${name} · ${code}`,
        searchLabel: `${name} ${code}`.trim()
      }));
    } catch {
      return [];
    }
  }, [opTypeMap]);

  const load = async () => {
    try {
      setLoading(true);
      const params = { ...form.getFieldsValue(), category };
      const resp = await getWorkOrderTemplates(params);
      const data = Array.isArray(resp?.results) ? resp.results : (Array.isArray(resp) ? resp : []);
      setRows(data);
    } catch {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };
  useEffect(()=>{ load(); }, []);
  useEffect(()=>{ load(); }, [category]);
  // 实时筛选：表单变化即刷新（简易防抖）
  const debounceRef = React.useRef<number | null>(null);
  const handleValuesChange = () => {
    try { if (debounceRef.current) (window as any).clearTimeout(debounceRef.current); } catch {}
    debounceRef.current = (window as any).setTimeout(() => { load(); }, 200);
  };
  // 载入操作类型字典（code -> name）
  useEffect(() => {
    (async () => {
      try {
        const req = await (await import('../../../../utils/request')).default.get('/api/v1/billing/operation-types/', { params: { all: 1 } });
        const data = req?.data;
        const list = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
        const map: Record<string, string> = {};
        (list || []).forEach((o:any) => {
          const code = String(o?.system_code || '').trim();
          const name = String(o?.name || '').trim();
          if (code) map[code] = name || code;
        });
        setOpTypeMap(map);
      } catch {
        setOpTypeMap({});
      }
    })();
  }, []);

  // 加载客户/项目下拉
  const ensureCustomers = async () => {
    if (customerOptions.length > 0) return;
    setCustomerLoading(true);
    try {
      const list = await getCustomerList({ onlyMain: true } as any);
      const opts = (list || []).map((c:any) => ({
        value: Number(c.id),
        label: `${c.name || c.username || ''}${(c.customer_code || c.profile?.customer_code) ? ' · ' + (c.customer_code || c.profile?.customer_code) : ''}`.trim()
      }));
      setCustomerOptions(opts);
      // 同步可读映射
      const map: Record<number, string> = {};
      (list || []).forEach((c:any) => {
        const id = Number(c.id);
        if (!Number.isNaN(id)) {
          const label = `${c.name || c.username || ''}${(c.customer_code || c.profile?.customer_code) ? ' · ' + (c.customer_code || c.profile?.customer_code) : ''}`.trim();
          map[id] = label || String(id);
        }
      });
      setCustomerLabelById(map);
    } catch { setCustomerOptions([]); } finally { setCustomerLoading(false); }
  };
  const setProjectOptionsSafe = (opts: Array<{ value: number; label: string }>) => setProjectOptions(opts);
  const loadProjectsByCustomer = async (cid?: number) => {
    const id = Number(cid);
    if (!Number.isFinite(id)) { setProjectOptionsSafe([]); return; }
    setProjectLoading(true);
    try {
      const resp:any = await getProjectList({ page: 1, page_size: 500, customer: id } as any);
      const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
      const opts = (rows || []).map((p:any) => ({ value: Number(p.id), label: `${p.project_code || p.id} - ${p.name || ''}` }));
      setProjectOptionsSafe(opts);
    } catch { setProjectOptionsSafe([]); } finally { setProjectLoading(false); }
  };

  // 初始加载全量可读映射（客户/项目）用于列表展示（尽力而为）
  useEffect(() => {
    (async () => {
      try {
        const list = await getCustomerList({ onlyMain: true } as any);
        const map: Record<number, string> = {};
        (list || []).forEach((c:any) => {
          const id = Number(c.id);
          if (!Number.isNaN(id)) {
            const label = `${c.name || c.username || ''}${(c.customer_code || c.profile?.customer_code) ? ' · ' + (c.customer_code || c.profile?.customer_code) : ''}`.trim();
            map[id] = label || String(id);
          }
        });
        setCustomerLabelById(map);
      } catch {}
    })();
    (async () => {
      try {
        const resp:any = await getProjectList({ page: 1, page_size: 1000 } as any);
        const rows = Array.isArray(resp?.data?.results) ? resp.data.results : (Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []));
        const map: Record<number, string> = {};
        (rows || []).forEach((p:any) => {
          const id = Number(p.id);
          if (!Number.isNaN(id)) {
            const code = p.project_code || p.id;
            const name = p.name || '';
            map[id] = name ? `${code} - ${name}` : String(code);
          }
        });
        setProjectLabelById(map);
      } catch {}
    })();
  }, []);

  // 分组颜色：按来源指令模板的首个 system_code（排序后取首个）作为组键，依出现顺序交替底色
  const groupIndexByTplKey = useMemo(() => {
    const map = new Map<string, number>();
    let idx = 0;
    try {
      (rows || []).forEach((r:any) => {
        const arr = Array.isArray(r?.instruction_templates) ? r.instruction_templates : [];
        let key = 'NA';
        if (arr.length > 0) {
          const sorted = arr
            .map((it:any) => String(it?.system_code || it?.id || '').trim())
            .filter(Boolean)
            .sort();
          key = sorted[0] || 'NA';
        }
        if (!map.has(key)) {
          map.set(key, idx);
          idx += 1;
        }
      });
    } catch {}
    return map;
  }, [rows]);

  const columns = useMemo(() => {
    const base = [
    { title: '编码', dataIndex: 'code', key: 'code', width: 200, render: (v:any) => (<span title={String(v||'').trim()}>{String(v||'').trim()}</span>) },
    { title: '名称', dataIndex: 'name', key: 'name', width: 300, render: (v:any) => (<span title={String(v||'').trim()}>{String(v||'').trim()}</span>) },
    { title: '操作类型', dataIndex: 'operation_type', key: 'operation_type', width: 200, render: (v:any, r:any) => {
      const code = String(v || '').trim();
      const prefer = r?.operation_type_name;
      const name = prefer || opTypeMap[code] || code || '-';
      const title = code || (prefer ? `${prefer}` : '');
      return (<span title={title}>{name}</span>);
    }},
    ...(category === 'customer' ? [{
      title: '来源级别', key: 'src_level', width: 100, render: (_:any, r:any) => {
        const arr = Array.isArray(r?.instruction_templates) ? r.instruction_templates : [];
        const levels = Array.from(new Set(arr.map((it:any) => String(it?.level || '').trim()).filter(Boolean))) as string[];
        if (levels.length === 0) return <span style={{ color:'#999' }}>-</span>;
        const label = (lv:string) => lv==='system' ? '系统级' : (lv==='customer' ? '客户级' : (lv==='project' ? '项目级' : lv));
        const styleFor = (lv:string): React.CSSProperties => {
          if (lv === 'system') return { background:'#e6f4ff', border:'1px solid #91caff', color:'#0958d9' };
          if (lv === 'customer') return { background:'#f6ffed', border:'1px solid #b7eb8f', color:'#389e0d' };
          if (lv === 'project') return { background:'#fffbe6', border:'1px solid #ffe58f', color:'#ad6800' };
          return { background:'#f0f0f0', border:'1px solid #d9d9d9', color:'#595959' };
        };
        return (
          <Space size={4}>
            {levels.map((lv, i) => (
              <span key={i} style={{ display:'inline-block', padding:'0 8px', lineHeight:'22px', borderRadius:4, ...styleFor(lv) }}>
                {label(lv)}
              </span>
            ))}
          </Space>
        );
      }
    }] : []),
    ...(category === 'customer' ? [{
      title: '来源客户', key: 'src_customer', width: 100, render: (_:any, r:any) => {
        const arr = Array.isArray(r?.instruction_templates) ? r.instruction_templates : [];
        const ids = Array.from(new Set(arr.map((it:any) => Number(it?.customer_id)).filter((n:number)=> Number.isFinite(n))).values()) as number[];
        if (ids.length === 0) return null;
        const labels = ids.map((id) => {
          const opt = customerOptions.find(o => Number(o.value)===id);
          const text = customerLabelById[id] || opt?.label || '';
          return text && text.trim() ? text : null;
        }).filter(Boolean) as string[];
        if (labels.length === 0) return null;
        return <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
          {labels.map((text, i) => (<span key={i} title={text}>{text}</span>))}
        </div>;
      }
    }] : []),
    ...(category === 'customer' ? [{
      title: '来源项目', key: 'src_project', width: 150, render: (_:any, r:any) => {
        const arr = Array.isArray(r?.instruction_templates) ? r.instruction_templates : [];
        const ids = Array.from(new Set(arr.map((it:any) => Number(it?.project_id)).filter((n:number)=> Number.isFinite(n))).values()) as number[];
        if (ids.length === 0) return null;
        const items = ids.map((id) => {
          const opt = projectOptions.find(o => Number(o.value)===id);
          const label = projectLabelById[id] || opt?.label || '';
          const raw = String(label || '').trim();
          if (!raw) return null;
          let code = ''; let name = '';
          if (raw.includes(' - ')) {
            const parts = raw.split(' - ');
            code = String(parts[0] || '').trim();
            name = String(parts.slice(1).join(' - ') || '').trim();
          } else {
            name = raw;
          }
          return { id, code, name, label: raw };
        }).filter(Boolean) as Array<{ id:number; code:string; name:string; label:string }>;
        if (items.length === 0) return null;
        return (
          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {items.map((it, i) => (
              <div key={i} title={it.label} style={{ display:'flex', flexDirection:'column' }}>
                <span style={{ color:'#141414' }}>{it.name || '-'}</span>
                <span style={{ color:'#1677ff' }}>{it.code || ''}</span>
              </div>
            ))}
          </div>
        );
      }
    }] : []),
    ...(category === 'customer' ? [{
      title: '指令模板', key: 'instruction_templates', width: 280, render: (_:any, r:any) => {
        const arr = Array.isArray(r.instruction_templates) ? r.instruction_templates : [];
        if (!arr.length) return <span style={{ color:'#999' }}>-</span>;
        return (
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {arr.map((it:any, i:number) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span title={it?.system_code || ''}>
                  <Tag>{it?.name || '-'}</Tag>
                </span>
              </div>
            ))}
          </div>
        );
      }
    }] : []),
    { title: '启用', dataIndex: 'is_active', key: 'is_active', width: 90, render: (v:any)=> v ? <Tag color="green">是</Tag> : <Tag>否</Tag> },
    { title: '操作', key: 'ops', width: 120, render: (_:any, r:any) => (
      <Space size={4}>
        <Button size="small" type="link" onClick={()=> navigate(`/dashboard/admin/work-orders/templates/${r.id}/edit`)}>编辑</Button>
      </Space>
    )}
  ] as any[];
    return base;
  }, [navigate, category, opTypeMap, customerOptions, projectOptions]);

  const loadInstructionTemplates = async () => {
    if (insTplOptions.length > 0) return;
    setInsTplLoading(true);
    try {
      let list:any[] = [];
      const endpoints = ['/api/v1/instruction/templates/'];
      try {
        // 先尝试 all=1
        const resp = await (await import('../../../../utils/request')).default.get(endpoints[0], { params: { is_active: true, all: 1 } });
        const data = resp?.data;
        list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      } catch {}
      if (!list || list.length === 0) {
        // 分页兜底
        const pageSize = 200; let page = 1; const all:any[] = [];
        while (true) {
          try {
            const resp = await (await import('../../../../utils/request')).default.get(endpoints[0], { params: { page, page_size: pageSize, is_active: true } });
            const results = Array.isArray(resp?.data?.results) ? resp.data.results : [];
            if (!results || results.length === 0) break;
            all.push(...results);
            if (results.length < pageSize) break;
            page += 1; if (page > 1000) break;
          } catch { break; }
        }
        list = all;
      }
      const opts = (list || []).map((t:any) => {
        const code = String(t.system_code || t.code || '').trim();
        const name = String(t.name || '-').trim();
        return {
          value: String(t.id),
          label: (<span title={code}>{name}</span>) as unknown as any,
          searchLabel: `${name} ${code}`.trim()
        };
      }).filter((o:any) => !!o.value);
      setInsTplOptions(opts);
    } finally { setInsTplLoading(false); }
  };

  return (
    <div className="list-page-container">
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {category === 'internal' ? '仓内工单模板列表' : '客户工单模板列表'}
          </div>
          <Space>
            <Button.Group>
              <Button
                type="default"
                style={category === 'customer'
                  ? { background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 600 }
                  : {}}
                onClick={() => setCategory('customer')}
              >
                客户工单模板
              </Button>
              <Button
                type="default"
                style={category === 'internal'
                  ? { background: '#fff7e6', borderColor: '#ffe58f', color: '#ad6800', fontWeight: 600 }
                  : {}}
                onClick={() => setCategory('internal')}
              >
                仓内工单模板
              </Button>
            </Button.Group>
            <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={()=> navigate(`/dashboard/admin/work-orders/templates/create?category=${category}`)}>
              新增{category==='internal' ? '仓内工单模板' : '客户工单模板'}
            </Button>
          </Space>
        </div>
        <Form form={form} layout="horizontal" onFinish={load} onValuesChange={handleValuesChange}>
          <Row gutter={[12,12]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="operation_type" label="操作类型">
                <Select
                  allowClear
                  showSearch
                  placeholder="选择操作类型（名称或编码）"
                  options={opTypeOptions as any}
                  filterOption={(input, option:any) => {
                    const text = String(option?.searchLabel || option?.label || '').toLowerCase();
                    return text.includes(String(input || '').toLowerCase());
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item name="is_active" label="启用">
                <Select allowClear placeholder="全部">
                  <Option value={true as any}>启用</Option>
                  <Option value={false as any}>停用</Option>
                </Select>
              </Form.Item>
            </Col>
            {category === 'customer' ? (
              <Col xs={24} sm={12} md={8} lg={8}>
                <Form.Item name="instruction_template_id" label="指令模板">
                  <Select
                    allowClear
                    showSearch
                    placeholder="选择指令模板"
                    options={insTplOptions}
                    loading={insTplLoading}
                    onFocus={() => loadInstructionTemplates()}
                    filterOption={(input, option:any) => {
                      const text = String(option?.searchLabel || '').toLowerCase();
                      return text.includes(String(input || '').toLowerCase());
                    }}
                  />
                </Form.Item>
              </Col>
            ) : null}
            {category === 'customer' ? (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Form.Item name="instruction_template_level" label="来源模板级别">
                  <Select
                    allowClear
                    placeholder="全部级别"
                    options={[
                      { value: 'system', label: '系统级' },
                      { value: 'customer', label: '客户级' },
                      { value: 'project', label: '项目级' },
                    ]}
                  />
                </Form.Item>
              </Col>
            ) : null}
            {/* 当选择客户级或项目级时，显示来源客户筛选 */}
            <Form.Item shouldUpdate noStyle>
              {() => {
                const level = form.getFieldValue('instruction_template_level');
                if (category !== 'customer') return null;
                if (level === 'customer' || level === 'project') {
                  return (
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Form.Item name="instruction_template_customer_id" label="来源客户">
                        <Select
                          allowClear
                          showSearch
                          placeholder="选择客户"
                          loading={customerLoading}
                          onFocus={ensureCustomers}
                          options={customerOptions}
                          filterOption={(input, option:any) => String(option?.label || '').toLowerCase().includes(String(input || '').toLowerCase())}
                          onChange={(cid:number) => {
                            // 切换客户时清空项目筛选
                            form.setFieldsValue({ instruction_template_project_id: undefined });
                            setProjectOptions([]);
                            if (level === 'project' && Number.isFinite(Number(cid))) loadProjectsByCustomer(Number(cid));
                          }}
                        />
                      </Form.Item>
                    </Col>
                  );
                }
                return null;
              }}
            </Form.Item>
            {/* 当选择项目级时，显示项目筛选（依赖客户） */}
            <Form.Item shouldUpdate noStyle>
              {() => {
                const level = form.getFieldValue('instruction_template_level');
                const cid = form.getFieldValue('instruction_template_customer_id');
                if (category !== 'customer' || level !== 'project') return null;
                return (
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="instruction_template_project_id" label="来源项目">
                      <Select
                        allowClear
                        showSearch
                        placeholder={cid ? '选择项目' : '请先选择客户'}
                        disabled={!cid}
                        loading={projectLoading}
                        onFocus={() => { if (cid) loadProjectsByCustomer(Number(cid)); }}
                        options={projectOptions}
                        filterOption={(input, option:any) => String(option?.label || '').toLowerCase().includes(String(input || '').toLowerCase())}
                      />
                    </Form.Item>
                  </Col>
                );
              }}
            </Form.Item>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item name="keyword" label="关键字">
                <Input allowClear placeholder="编码/名称" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item>
                <Space>
                  <Button type="primary" icon={<SearchOutlined />} htmlType="submit">搜索</Button>
                  <Button icon={<ReloadOutlined />} onClick={()=> { form.resetFields(); load(); }}>重置</Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          size="small"
          tableLayout="fixed"
          rowKey={(r:any)=> String(r.id)}
          loading={loading}
          dataSource={rows}
          columns={columns as any}
          pagination={false}
          scroll={{ x: 1000 }}
          onRow={(r:any) => {
            const arr = Array.isArray(r?.instruction_templates) ? r.instruction_templates : [];
            let key = 'NA';
            if (arr.length > 0) {
              const sorted = arr
                .map((it:any) => String(it?.system_code || it?.id || '').trim())
                .filter(Boolean)
                .sort();
              key = sorted[0] || 'NA';
            }
            const idx = groupIndexByTplKey.get(key) ?? 0;
            const palette = [
              { bg: '#e6f7ff', border: '#91d5ff' },   // 浅蓝
              { bg: '#fff7e6', border: '#ffd591' },   // 浅橙
              { bg: '#f6ffed', border: '#b7eb8f' },   // 浅绿
              { bg: '#f9f0ff', border: '#d3adf7' },   // 浅紫
            ];
            const item = palette[idx % palette.length];
            return { style: { background: item.bg, borderLeft: `4px solid ${item.border}` } } as any;
          }}
        />
      </Card>
    </div>
  );
};

export default TemplateListPage;


